
/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { GridCellModes, GridCellModesModel, GridCellParams, GridEventListener, GridRowEditStopReasons, GridRowModel, GridRowModesModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import { setScheduledTask } from "../../../../store/api/tasks/reducer";
import { useStoreDispatch } from "../../../../store/hooks";
import CustomEditDataGrid from "../../../Shared/Table/CustomEditDataGrid";
import { setDebugLevelColumns } from "../../../Shared/Table/Columns";
import { Dispatch, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { rowModesModelSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg, setOpenToast, setRowModesModel } from "../../../../store/helpers/helperReducers";
import { AnyAction } from "@reduxjs/toolkit";
import { ThunkDispatch } from "redux-thunk";
import { Plus } from "lucide-react";
import { Button } from "../../../ui/button";
import * as uuid from "uuid";
import { isEditTaskSelector, tasksSelector } from "../../../../store/api/tasks/selector";
import { setDebugArrayToSting } from "../../helpers";
import { JAVA_MAX_VALUE } from "../../../../globals/globals";
import { ISetDebug } from "../../../../store/api/tasks/types";

interface EditToolbarProps {
    dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<AnyAction>;
    setCellModesModel: any;
    oldRows: ISetDebug[];
    oldModel: GridCellModesModel;
}

const EditToolbar = (props: EditToolbarProps) => {
    const { dispatch, setCellModesModel, oldRows = [] } = props;

    const handleClickOnAddRecord = () => {
        const id = uuid.v4(); // generate the unique id for each row
        dispatch(setScheduledTask({ setDebugRows: [{ id: id, module: "-", level: "-", group: "-", nodeId: "0", isNew: true }, ...oldRows,] }));
        setCellModesModel((oldModel: GridCellModesModel) => ({
            ...oldModel,
            [id]: { mode: GridCellModes.Edit, fieldToFocus: 'module' },
        }));

    };

    return (
        <GridToolbarContainer sx={{ justifyContent: "space-between", pb: 3 }}>
            <p className="text-base font-medium">Set Debug Level</p>
            <Button variant="ghost" size="sm" onClick={handleClickOnAddRecord}>
                <Plus className="h-4 w-4 mr-1" />
                Add record
            </Button>
        </GridToolbarContainer>
    );
}
export const SetDebugLevel: React.FC = () => {
    const dispatch = useStoreDispatch();
    const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});


    const rowModesModel = useSelector(rowModesModelSelector) || {};
    const { setDebug, setDebugRows } = useSelector(tasksSelector);
    const isEditTask = useSelector(isEditTaskSelector);

    useEffect(() => {
        if (isEditTask && setDebug.length > 0) {
            let rowsArrayStr: string[] = [];
            let cellArrayStr: string[] = [];
            let rowData: ISetDebug[] = [];
            if (setDebug.includes("|")) {
                rowsArrayStr = setDebug.split("|");
                rowsArrayStr.forEach((rowItem: string) => {
                    cellArrayStr = rowItem.split(",");
                    rowData = [...rowData, { id: uuid.v4(), module: cellArrayStr[0], group: cellArrayStr[1], level: cellArrayStr[2], nodeId: cellArrayStr[3], isNew: false }]
                })
            } else {
                cellArrayStr = setDebug.split(",");
                rowData = [{ id: uuid.v4(), module: cellArrayStr[0], group: cellArrayStr[1], level: cellArrayStr[2], nodeId: cellArrayStr[3], isNew: false }];
            }
            dispatch(setScheduledTask({ setDebugRows: rowData }));
        }
    }, [])

    //const handleCellEditStop = (params: GridCellEditStopParams, event: MuiEvent) => {
    // if (params.reason === GridCellEditStopReasons.cellFocusOut) {
    //     event.defaultMuiPrevented = true;
    // }

    //}
    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        dispatch(setRowModesModel(newRowModesModel));
    };
    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const processRowUpdate = useCallback(
        async (newRow: GridRowModel, oldRow: GridRowModel) => {
            let updatedRow: any = {};
            if ((!!newRow?.nodeId && !!!newRow?.nodeId.trim()) || (isNaN(newRow.nodeId) || Number(newRow.nodeId) < 0 || Number(newRow.nodeId) > JAVA_MAX_VALUE)) {
                dispatch(
                    setOpenToast({
                        openToast: true,
                        toastType: "error",
                        message: `The Node ID column for row (${newRow.module},${newRow.level},${newRow.group}) is Invalid!`,
                    })
                );
                dispatch(setErrorMsg({ nodeId: "Invalid Node ID!" }));
                updatedRow = { id: newRow.id, nodeId: 0 };
                setTimeout(() => dispatch(setErrorMsg({ nodeId: "" })), 3000);
            } else {
                const allreadyExist = setDebugRows?.some((item: ISetDebug) => item.module === newRow.module && item.group === newRow.group && item.level === newRow.level && item.nodeId === newRow.nodeId);
                const isNotSameRow = (oldRow.module === newRow.module && oldRow.group === newRow.group && oldRow.level === newRow.level && oldRow.nodeId === newRow.nodeId);
                dispatch(setErrorMsg({ nodeId: "", row: "" }));
                if (allreadyExist && !isNotSameRow) {
                    updatedRow = { id: newRow.id }
                    dispatch(setErrorMsg({ row: `Duplicate Entry. The new row was already exists with the same values!` }));
                    dispatch(
                        setOpenToast({
                            openToast: true,
                            toastType: "error",
                            message: `Duplicate Entry. The new row was already exists with the same values!`,
                        })
                    );
                    setTimeout(() => dispatch(setErrorMsg({ row: "" })), 3000);
                } else {
                    updatedRow = { ...newRow, group: newRow.module !== oldRow.module ? "-" : newRow.group, isNew: false };
                    const latestRows = setDebugRows?.map((row: ISetDebug) => (row.id === newRow.id ? updatedRow : row)) || [];
                    dispatch(setScheduledTask({ setDebug: setDebugArrayToSting(latestRows), setDebugRows: latestRows }));
                }

            }
            return updatedRow;

        }, [dispatch, setDebugRows],
    );
    const handleProcessRowUpdateError = useCallback((error: Error) => {
        dispatch(
            setOpenToast({
                openToast: true,
                toastType: "error",
                message: error.toString(),
            })
        );
    }, [dispatch]);

    const handleCellModesModelChange = useCallback(
        (newModel: GridCellModesModel) => {
            setCellModesModel(newModel);
        }, [],
    );
    const handleCellClick = useCallback(
        (params: GridCellParams, event: React.MouseEvent) => {
            if (!params.isEditable) {
                return;
            }

            // Ignore portal
            if (
                (event.target as any).nodeType === 1 &&
                !event.currentTarget.contains(event.target as Element)
            ) {
                return;
            }

            setCellModesModel((prevModel) => {

                return {
                    // Revert the mode of the other cells from other rows
                    ...Object.keys(prevModel).reduce(
                        (acc, id) => ({
                            ...acc,
                            [id]: Object.keys(prevModel[id]).reduce(
                                (acc2, field) => ({
                                    ...acc2,
                                    [field]: { mode: GridCellModes.View },
                                }),
                                {},
                            ),
                        }),
                        {},
                    ),
                    [params.id]: {
                        // Revert the mode of other cells in the same row
                        ...Object.keys(prevModel[params.id] || {}).reduce(
                            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
                            {},
                        ),
                        [params.field]: { mode: GridCellModes.Edit },
                    },
                };
            });
        },
        [],
    );
    return (
        <CustomEditDataGrid
            sx={{
                "& .MuiSvgIcon-root.MuiSelect-icon": {
                    color: "var(--muted-foreground)",
                },
            }}
            columns={setDebugLevelColumns}
            rows={setDebugRows}
            hideFooter={true}
            editMode="cell"
            getRowId={(row) => row.id}
            disableColumnMenu={true}
            slots={{
                toolbar: EditToolbar,
            }}
            slotProps={{
                toolbar: { dispatch, setCellModesModel, oldRows: setDebugRows, oldModel: cellModesModel },
            }}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            cellModesModel={cellModesModel}
            // onCellEditStop={handleCellEditStop}
            onCellModesModelChange={handleCellModesModelChange}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onCellClick={handleCellClick}
        />
    )
};